import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// SECURITY: User context import available for future user-aware cache operations
// import { useUserContext } from './useUserContext';

// Cache invalidation strategies
type InvalidationStrategy = 'conservative' | 'aggressive' | 'targeted';

interface CacheInvalidationOptions {
  strategy?: InvalidationStrategy;
  refetchActive?: boolean;
  includeRelated?: boolean;
  delay?: number;
  userId?: string; // SECURITY: Optional user context override
}

interface UseCacheCoordinatorReturn {
  invalidateTradeRelatedData: (
    changeType: 'new_trades' | 'updated_trades' | 'all',
    options?: CacheInvalidationOptions
  ) => Promise<void>;
  invalidateSpecificQuery: (queryKey: unknown[], options?: CacheInvalidationOptions) => Promise<void>;
  prefetchCommonQueries: () => Promise<void>;
  getCacheStatus: () => {
    totalQueries: number;
    activeQueries: number;
    staleQueries: number;
    cacheSize: string;
  };
  clearAllCache: () => void;
}

/**
 * Centralized cache coordination for trade-related data
 * Provides intelligent cache invalidation strategies and coordination
 */
export const useCacheCoordinator = (): UseCacheCoordinatorReturn => {
  const queryClient = useQueryClient();
  
  // SECURITY: User context available for future user-aware cache operations
  // const { userId: currentUserId, isReady: isUserContextReady } = useUserContext();

  /**
   * Invalidate trade-related data with smart coordination
   */
  const invalidateTradeRelatedData = useCallback(async (
    changeType: 'new_trades' | 'updated_trades' | 'all',
    options: CacheInvalidationOptions = {}
  ) => {
    const {
      strategy = 'targeted',
      refetchActive = true,
      includeRelated = true,
      delay = 0
    } = options;

    // Optional delay for batching invalidations
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    console.log(`🔄 Cache invalidation started:`, {
      changeType,
      strategy,
      refetchActive,
      includeRelated
    });

    const invalidationPromises: Promise<void>[] = [];

    try {
      // Core trade data - always invalidate
      invalidationPromises.push(
        queryClient.invalidateQueries({
          queryKey: ['closedTrades'],
          refetchType: refetchActive ? 'active' : 'none'
        })
      );      // Filter options - invalidate when new trades might introduce new symbols/accounts/strategies
      if (strategy === 'aggressive' || (changeType === 'new_trades' && includeRelated)) {
        invalidationPromises.push(
          // TODO: Update to use user-aware keys when userId is available
          queryClient.invalidateQueries({ queryKey: ['tradeSymbols'] }),
          queryClient.invalidateQueries({ queryKey: ['tradeAccounts'] }),
          queryClient.invalidateQueries({ queryKey: ['tradeStrategies'] })
        );
      }

      // Related dashboard data - invalidate when trade counts/values change
      if (includeRelated && (changeType === 'new_trades' || changeType === 'all')) {
        switch (strategy) {          case 'aggressive':
            // Invalidate everything related
            invalidationPromises.push(
              // TODO: Update to use user-aware keys when userId is available
              queryClient.invalidateQueries({ queryKey: ['accountSummary'] }),
              queryClient.invalidateQueries({ queryKey: ['strategySummary'] }),
              queryClient.invalidateQueries({ queryKey: ['accountPnlSummary'] }),
              queryClient.invalidateQueries({ queryKey: ['strategyPnlSummary'] }),
              queryClient.invalidateQueries({ queryKey: ['pnlTimeframes'] }),
              queryClient.invalidateQueries({ queryKey: ['accountDaily'] }),
              queryClient.invalidateQueries({ queryKey: ['strategyDaily'] }),
              queryClient.invalidateQueries({ queryKey: ['monthlyCalendar'] }),
              queryClient.invalidateQueries({ queryKey: ['accountBalances'] })
            );
            break;          case 'targeted':
            // Only invalidate directly affected summaries
            invalidationPromises.push(
              // TODO: Update to use user-aware keys when userId is available
              queryClient.invalidateQueries({ queryKey: ['accountSummary'] }),
              queryClient.invalidateQueries({ queryKey: ['strategySummary'] }),
              queryClient.invalidateQueries({ queryKey: ['pnlTimeframes'] })
            );
            break;

          case 'conservative':
            // Only invalidate high-level summaries
            invalidationPromises.push(
              queryClient.invalidateQueries({ queryKey: ['pnlTimeframes'] })
            );
            break;
        }
      }

      // Execute all invalidations concurrently
      await Promise.all(invalidationPromises);

      console.log(`✅ Cache invalidation completed:`, {
        invalidatedQueries: invalidationPromises.length,
        changeType,
        strategy
      });

    } catch (error) {
      console.error('❌ Cache invalidation failed:', error);
      throw error;
    }
  }, [queryClient]);

  /**
   * Invalidate a specific query with options
   */
  const invalidateSpecificQuery = useCallback(async (
    queryKey: unknown[],
    options: CacheInvalidationOptions = {}
  ) => {
    const { refetchActive = true, delay = 0 } = options;

    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    try {
      await queryClient.invalidateQueries({
        queryKey,
        refetchType: refetchActive ? 'active' : 'none'
      });

      console.log(`✅ Specific query invalidated:`, queryKey);
    } catch (error) {
      console.error('❌ Specific query invalidation failed:', error, queryKey);
      throw error;
    }
  }, [queryClient]);

  /**
   * Prefetch commonly accessed queries for better performance
   */
  const prefetchCommonQueries = useCallback(async () => {
    console.log('🚀 Prefetching common queries...');

    try {
      const prefetchPromises = [
        // Prefetch first page of all trades
        queryClient.prefetchQuery({
          queryKey: ['closedTrades', { symbol: null, accountname: null, strategyname: null, limit: 20, offset: 0 }],
          staleTime: 5 * 60 * 1000 // 5 minutes
        }),
        
        // Prefetch filter options
        queryClient.prefetchQuery({
          queryKey: ['tradeSymbols', 'All', 'All'],
          staleTime: 10 * 60 * 1000 // 10 minutes
        }),
        
        queryClient.prefetchQuery({
          queryKey: ['tradeAccounts', 'All', 'All'],
          staleTime: 10 * 60 * 1000 // 10 minutes
        }),
        
        queryClient.prefetchQuery({
          queryKey: ['tradeStrategies', 'All', 'All'],
          staleTime: 10 * 60 * 1000 // 10 minutes
        })
      ];

      await Promise.allSettled(prefetchPromises);
      console.log('✅ Common queries prefetched');
    } catch (error) {
      console.error('❌ Prefetch failed:', error);
    }
  }, [queryClient]);

  /**
   * Get current cache status for debugging/monitoring
   */
  const getCacheStatus = useCallback(() => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    const activeQueries = queries.filter(query => query.getObserversCount() > 0);
    const staleQueries = queries.filter(query => query.isStale());
    
    // Estimate cache size (rough approximation)
    const cacheSize = `${Math.round(queries.length * 0.1)}KB (estimated)`;

    return {
      totalQueries: queries.length,
      activeQueries: activeQueries.length,
      staleQueries: staleQueries.length,
      cacheSize
    };
  }, [queryClient]);

  /**
   * Clear all cache (nuclear option for debugging)
   */
  const clearAllCache = useCallback(() => {
    console.log('🧹 Clearing all cache...');
    queryClient.clear();
    console.log('✅ All cache cleared');
  }, [queryClient]);

  return {
    invalidateTradeRelatedData,
    invalidateSpecificQuery,
    prefetchCommonQueries,
    getCacheStatus,
    clearAllCache
  };
};

export default useCacheCoordinator;
